/**
 * The model ("subagente") of the agent that runs here (the owner's ask, 15/10): a quiet select on the
 * prompt box's "Roda nesta máquina, com …" line, like choosing Fable in Claude Code. "Padrão" keeps
 * the agent's own default — no flag, exactly as before. Claude Code offers its aliases; OpenCode the
 * local catalog's models that truly served; any agent takes "Outro…" typed by hand. The choice is
 * per person and per machine, saved with the runner's settings, and the desktop validates it again.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { agentProgram, type LocalAgentId } from '../lib/localAgent'
import { readRunnerSettings, saveRunnerSettings } from '../lib/runnerHost'

/** The models each agent is offered by name; "Outro…" covers the rest. */
export const MODEL_PRESETS: Partial<Record<LocalAgentId, string[]>> = {
  claude: ['sonnet', 'opus', 'haiku'],
}

const VALID = /^[A-Za-z0-9._:/-]{1,80}$/

export function AgentModelPicker({ agent }: { agent: LocalAgentId }) {
  const { t } = useI18n()
  const [model, setModel] = useState<string>(() => readRunnerSettings().models?.[agent] ?? '')
  const [typing, setTyping] = useState(false)
  const [typed, setTyped] = useState('')
  const { data: catalog } = useQuery({ queryKey: ['local-models'], queryFn: api.localModels, enabled: agent === 'opencode', staleTime: 10 * 60_000 })

  const presets = agent === 'opencode'
    ? (catalog?.models ?? []).filter((m) => m.status === 'tested').map((m) => m.tag)
    : MODEL_PRESETS[agentProgram(agent)] ?? []
  const options = model && !presets.includes(model) ? [...presets, model] : presets

  const choose = (next: string) => {
    const settings = readRunnerSettings()
    const models = { ...(settings.models ?? {}) }
    if (next) models[agent] = next
    else delete models[agent]
    saveRunnerSettings({ ...settings, models })
    setModel(next)
  }

  if (typing) {
    return (
      <span className="row row--tight agent-model">
        <input
          className="input input--sm mono"
          autoFocus
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && VALID.test(typed.trim())) {
              choose(typed.trim())
              setTyping(false)
            }
            if (e.key === 'Escape') setTyping(false)
          }}
          placeholder={t('agentModel.typeHint')}
          aria-label={t('agentModel.label')}
          style={{ width: 150 }}
        />
      </span>
    )
  }

  return (
    <select
      className="select select--sm agent-model"
      value={model}
      onChange={(e) => {
        if (e.target.value === '__other') {
          setTyped(model)
          setTyping(true)
        } else {
          choose(e.target.value)
        }
      }}
      aria-label={t('agentModel.label')}
      title={t('agentModel.label')}
    >
      <option value="">{t('agentModel.default')}</option>
      {options.map((m) => (
        <option key={m} value={m}>{m}</option>
      ))}
      <option value="__other">{t('agentModel.custom')}</option>
    </select>
  )
}
